import { getBusinessDateString } from './business-date.ts';
import { lockRateValue } from './lock-net-buy-rate.ts';

/**
 * Lock versions written by the OB cache refresh (ob-pickup-analysis).
 *
 * Encompass sync and this refresh save the same lock in different shapes.
 * Encompass sends calendar dates plus sell-side and documentation fields; OB
 * sends UTC timestamps, its own plan code and FICO, and a zero CLTV. The DB
 * writer's exact material hash never matches across the two shapes, so the
 * refresh skips its copy when the latest saved version already carries the
 * same lock economics.
 */

export function buildObRateLockFields(cacheRow: any): Record<string, unknown> {
  return {
    rate_lock_status: cacheRow.ob_status === 'Locked' ? 'Locked' : cacheRow.ob_status,
    rate_lock_buy_side_lock_date: cacheRow.lock_accepted_on || cacheRow.lock_request_date,
    rate_lock_buy_side_lock_expires: cacheRow.lock_expiration_date,
    rate_lock_buy_side_number_of_days: cacheRow.lock_period,
    rate_lock_buy_side_rate: cacheRow.locked_rate,
    rate_lock_total_buy_price: cacheRow.locked_price,
    rate_lock_price_total_adjustment: null,
    rate_lock_buy_side_adjustments: cacheRow.raw_product_payload?.adjustments ?? null,
    rate_lock_request_lock_date: cacheRow.lock_request_date,
    rate_lock_request_number_of_days: cacheRow.raw_loan_payload?.loanInformation?.desiredLockPeriod ?? null,
    rate_lock_loan_program: cacheRow.product_name,
    rate_lock_plan_code: cacheRow.product_id,
    rate_lock_pricing_updated: cacheRow.raw_product_payload?.pricingLastUpdated
      || cacheRow.raw_product_payload?.pricingDetail?.pricingLastUpdated
      || cacheRow.raw_loan_payload?.productSummary?.pricingLastUpdated
      || cacheRow.price_last_change_date,
    rate_lock_min_fico: cacheRow.representative_fico,
    rate_lock_min_fico_2: null,
    rate_lock_base_loan_amount: cacheRow.base_loan_amount,
    rate_lock_property_estimated_value: cacheRow.sales_price,
    rate_lock_property_appraised_value: cacheRow.appraised_value,
    rate_lock_documentation_type: cacheRow.raw_loan_payload?.loanInformation?.documentationType ?? null,
    rate_lock_prepay_penalty: cacheRow.raw_loan_payload?.loanInformation?.prepaymentPenalty ?? null,
    rate_lock_impound_waived: cacheRow.raw_loan_payload?.loanInformation?.waiveEscrows ?? null,
    rate_lock_self_employed_indicator: cacheRow.raw_loan_payload?.borrowerInformation?.borrowerFlags?.selfEmployed ?? null,
    rate_lock_first_time_homebuyer: cacheRow.raw_loan_payload?.borrowerInformation?.borrowerFlags?.firstTimeHomeBuyer ?? null,
    rate_lock_lender_paid_mi: null,
    rate_lock_ltv: cacheRow.ltv,
    rate_lock_cltv: cacheRow.cltv,
    rate_lock_hcltv: cacheRow.hcltv,
    rate_lock_mi_premium_percent: null,
    rate_lock_funding_amount: null,
    rate_lock_mip_paid_in_cash: null,
    rate_lock_sell_side_rate: null,
    rate_lock_total_sell_price: null,
    rate_lock_sell_side_lock_date: null,
    rate_lock_sell_side_lock_expires: null,
    rate_lock_investor_commitment: null,
    investor_name: cacheRow.investor_name,
    property_county: cacheRow.property_county,
  };
}

export function hasCompleteObLock(cacheRow: any): boolean {
  return cacheRow?.ob_status === 'Locked'
    && (cacheRow?.lock_accepted_on || cacheRow?.lock_request_date)
    && cacheRow?.locked_price != null
    && cacheRow?.locked_rate != null;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ZONED_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ].*(Z|[+-]\d{2}(:?\d{2})?)$/;
const WALL_CLOCK_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})[T ]/;

// Encompass stores lock dates as calendar dates; OB stores instants such as
// 2026-09-18T04:59:00+00:00, which is 09-17 in Pacific time. A zoned instant
// becomes its Pacific business date. A calendar date, or a timestamp with no
// zone, keeps its own date so it is never shifted a day.
export function lockDateKey(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (DATE_ONLY.test(text)) return text;
  if (ZONED_TIMESTAMP.test(text)) {
    // Postgres text output can end in an hour-only offset (+00) that Date rejects.
    const instant = new Date(text.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'));
    return Number.isNaN(instant.getTime()) ? null : getBusinessDateString(instant);
  }
  return text.match(WALL_CLOCK_TIMESTAMP)?.[1] ?? null;
}

function scaledInteger(value: unknown, places: number): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.round(numeric * 10 ** places) : null;
}

/**
 * The lock economics both writers agree on: total buy price, buy-side net rate
 * (Encompass 2160: OB reports it directly, Encompass as base rate plus BaseRate
 * adjustment lines), lock date, and expiration as a Pacific business date. Null when any of them
 * is missing, so incomplete data never counts as a match. Lock days are left
 * out: the two systems count them differently (OB ran 1 to 3 days above
 * Encompass on identical dates), and the two dates already pin the term.
 */
export function lockEconomicsKey(fields: Record<string, unknown> | null | undefined): string | null {
  if (!fields) return null;
  const key = [
    scaledInteger(fields.rate_lock_total_buy_price, 3),
    scaledInteger(lockRateValue(fields), 3),
    lockDateKey(fields.rate_lock_buy_side_lock_date),
    lockDateKey(fields.rate_lock_buy_side_lock_expires),
  ];
  return key.some((part) => part === null) ? null : JSON.stringify(key);
}

export function hasSameLockEconomics(
  candidate: Record<string, unknown> | null | undefined,
  latest: Record<string, unknown> | null | undefined,
): boolean {
  const candidateKey = lockEconomicsKey(candidate);
  return candidateKey !== null && candidateKey === lockEconomicsKey(latest);
}

export type ObLockVersionOutcome = 'not_eligible' | 'skipped_same_economics' | 'saved' | 'save_failed';

export async function ensureLockVersionFromObCache(supabase: any, cacheRow: any): Promise<ObLockVersionOutcome> {
  if (!cacheRow?.encompass_loan_id || !hasCompleteObLock(cacheRow)) return 'not_eligible';
  const rateLockFields = buildObRateLockFields(cacheRow);

  const { data: latest, error: latestError } = await supabase
    .from('lock_versions')
    .select('version, trigger_reason, rate_lock_fields')
    .eq('encompass_loan_id', cacheRow.encompass_loan_id)
    .eq('organization_id', cacheRow.organization_id)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (latestError) {
    // Fail open to the earlier behavior: the DB writer still dedupes exact hashes.
    console.warn(
      `Failed to load the latest lock version for ${cacheRow.encompass_loan_id}; saving the OB lock:`,
      latestError.message,
    );
  } else if (
    latest
    && latest.trigger_reason !== 'manual_capture'
    && hasSameLockEconomics(rateLockFields, latest.rate_lock_fields)
  ) {
    console.log(
      `OB lock version skipped for ${cacheRow.encompass_loan_id}: v${latest.version} already has these lock economics`,
    );
    return 'skipped_same_economics';
  }

  const { error } = await supabase.rpc('insert_lock_version_locked', {
    p_encompass_loan_id: cacheRow.encompass_loan_id,
    p_organization_id: cacheRow.organization_id,
    p_trigger_reason: 'pricing_change',
    p_rate_lock_fields: rateLockFields,
    p_source_lock_snapshot_id: null,
  });
  if (error) {
    console.warn(`Failed to ensure lock version from OB cache for ${cacheRow.encompass_loan_id}:`, error.message);
    return 'save_failed';
  }
  return 'saved';
}
